import { useCallback, useMemo } from "react";
import { useFetchMeQuery } from "features/auth/authAPI";

/**
 * Hook to check communication method permissions based on user's groups.
 *
 * Permission rules:
 * - If allowedGroupIds is empty/null, ALL users can use the communication method
 * - If allowedGroupIds has values, only users in those groups can use it
 * - Admin users (me.isAdmin) bypass all permission checks
 *
 * @returns {{ canUseCommunicationMethod: (method) => boolean, filterAllowedCommunicationMethods: (methods) => methods }}
 */
export function useCommunicationMethodPermissions() {
  const { data: me } = useFetchMeQuery();

  const userGroupIds = useMemo(() => me?.groupIds ?? [], [me?.groupIds]);
  const isAdmin = me?.isAdmin ?? false;

  /**
   * Check if user can use a communication method
   * @param {{ allowedGroupIds?: string[] }} method - The communication method
   * @returns {boolean}
   */
  const canUseCommunicationMethod = useCallback(
    (method) => {
      // Admin users can always use any method
      if (isAdmin) return true;

      // If no allowed groups specified, everyone can use it
      const allowedGroupIds = method?.allowedGroupIds;
      if (!allowedGroupIds || allowedGroupIds.length === 0) return true;

      // Check if user belongs to any of the allowed groups
      return allowedGroupIds.some((groupId) => userGroupIds.includes(groupId));
    },
    [isAdmin, userGroupIds],
  );

  /**
   * Filter communication methods to only include those the user can use
   * @param {Array} methods - Array of communication methods
   * @returns {Array} - Filtered array of allowed methods
   */
  const filterAllowedCommunicationMethods = useCallback(
    (methods) => {
      if (!methods) return [];
      return methods.filter(canUseCommunicationMethod);
    },
    [canUseCommunicationMethod],
  );

  return {
    canUseCommunicationMethod,
    filterAllowedCommunicationMethods,
  };
}

export default useCommunicationMethodPermissions;
